import { DownOutlined, LoadingOutlined } from "@ant-design/icons"
import { Badge, Button, Dropdown } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DownloadTray } from "../../../Icon/icons/download-tray.tsx"
import { FilePlus } from "../../../Icon/icons/file-plus.tsx"
import { UploadTray } from "../../../Icon/icons/upload-tray.tsx"
import { UsersPlus } from "../../../Icon/icons/users-plus.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { showErrorNotifications } from "../../../utils/handlers/handle-error"
import { DocumentCreateMenuOption } from "../../Lead/LeadWorkspacePage/documents/DocumentCreateMenuOption.tsx"
import { CreateProjectWizard } from "../../Project/ProjectAddPage/wizard/CreateProjectWizard.tsx"
import * as services from "../api/services"
import ProjectsLeadApproval from "./ProjectsLeadApproval.tsx"

const XLSX_ACCEPT =
  ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

interface ProjectsLeadApprovalTriggerProps {
  onRefetchCallback: () => void
  onImport: (file: File) => Promise<void>
  onDownloadTemplate: () => Promise<void>
}

export function ProjectsLeadApprovalTrigger({
  onRefetchCallback,
  onImport,
  onDownloadTemplate
}: ProjectsLeadApprovalTriggerProps) {
  const { t } = useTranslation()

  const canConvertLeadToProject = useAppSelector(
    (state) =>
      state?.auth?.userInfo?.permissions?.codes?.projects?.features
        ?.project_approval
  )

  const [menuOpen, setMenuOpen] = useState(false)
  const [openApprovalModal, setOpenApprovalModal] = useState(false)
  const [openWizard, setOpenWizard] = useState(false)
  // Covers both the file import and the template download — one is enough,
  // since a user cannot start the second while the first is in flight.
  const [busy, setBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // `null` is "not fetched", which the badge withholds rather than rendering
  // as a confident `0`. Local rather than in the projects slice: nothing else
  // reads it, and a slice field would have to be imported by the reducer,
  // which every page-level test that mocks `projects.thunks` would then need
  // to know about.
  const [approvalCount, setApprovalCount] = useState<number | null>(null)

  const showCount = approvalCount !== null && approvalCount > 0

  // The same queue the modal lists, asked only for its size — `page_size: 1`
  // because nothing here reads the rows. Silent on failure on purpose: this
  // is a number on a menu item, and an error toast for it would be worse than
  // the badge simply not appearing.
  const refreshApprovalCount = useCallback(async () => {
    if (!canConvertLeadToProject) return

    const response = await services.getLeadsApproval({ page: 1, page_size: 1 })

    if (response.ok) setApprovalCount(response.data?.count ?? 0)
  }, [canConvertLeadToProject])

  // Up front rather than when the dropdown opens, so the number is already
  // right when the item appears instead of popping in a beat later.
  useEffect(() => {
    void refreshApprovalCount()
  }, [refreshApprovalCount])

  const onCloseHandler = (refetch = false) => {
    if (refetch) onRefetchCallback()
    setOpenApprovalModal(false)
    // Approving a lead is the one thing that empties this queue, and it only
    // happens inside the modal — so closing it is when the number is stale.
    void refreshApprovalCount()
  }

  const onWizardClose = () => setOpenWizard(false)

  const runBusy = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setBusy(false)
      setMenuOpen(false)
    }
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isXlsx =
      file.name.endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

    if (!isXlsx) {
      showErrorNotifications(t("projectCreate.importInvalidFile"))
    } else {
      void runBusy(() => onImport(file))
    }

    e.target.value = ""
  }

  const countBadge = showCount ? (
    <Badge count={approvalCount ?? 0} size={"small"} />
  ) : undefined

  return (
    <>
      <input
        ref={fileInputRef}
        type={"file"}
        accept={XLSX_ACCEPT}
        className={"hidden"}
        onChange={onFileChange}
      />

      <Dropdown
        trigger={["click"]}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        popupRender={() => (
          <div
            className={
              "flex w-96 flex-col gap-1 rounded-card border border-border bg-white p-2 shadow-lg"
            }
          >
            {canConvertLeadToProject && (
              <DocumentCreateMenuOption
                icon={<UsersPlus />}
                accent={"blue"}
                title={t("projectCreate.fromLead")}
                description={t("projectCreate.fromLeadDescription")}
                badge={countBadge}
                onClick={() => {
                  setMenuOpen(false)
                  setOpenApprovalModal(true)
                }}
              />
            )}
            <DocumentCreateMenuOption
              icon={<FilePlus />}
              accent={"green"}
              title={t("projectCreate.fromScratch")}
              description={t("projectCreate.fromScratchDescription")}
              onClick={() => {
                setMenuOpen(false)
                setOpenWizard(true)
              }}
            />

            <div className={"mx-1 my-1 border-t border-border"} />

            <DocumentCreateMenuOption
              icon={busy ? <LoadingOutlined /> : <UploadTray />}
              accent={"indigo"}
              title={t("projectCreate.import")}
              description={t("projectCreate.importDescription")}
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            />

            <div className={"mx-1 my-1 border-t border-border"} />

            <DocumentCreateMenuOption
              icon={busy ? <LoadingOutlined /> : <DownloadTray />}
              accent={"orange"}
              title={t("projectCreate.downloadTemplate")}
              description={t("projectCreate.downloadTemplateDescription")}
              disabled={busy}
              onClick={() => void runBusy(onDownloadTemplate)}
            />
          </div>
        )}
      >
        <Button type={"primary"} className={"w-auto"}>
          <span className={"inline-flex items-center gap-1.5"}>
            {t("projectCreate.newProject")}
            {countBadge}
            <span
              aria-hidden
              className={"mx-1 h-4 w-px bg-current opacity-30"}
            />
            <DownOutlined className={"text-xs"} />
          </span>
        </Button>
      </Dropdown>

      {/*@todo add lazy + suspense */}
      {openApprovalModal && <ProjectsLeadApproval onClose={onCloseHandler} />}
      {openWizard && <CreateProjectWizard onClose={onWizardClose} />}
    </>
  )
}
